import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { saveImage } from '../../utils/imageSave';
import { imageService } from '../../services/ImageService';

interface ImageLightboxProps {
  src: string;
  alt?: string;
  onClose: () => void;
  /** 同组图片原始 src 列表（多图时支持左右滑动切换） */
  images?: string[];
  /** 当前图片在 images 中的索引 */
  initialIndex?: number;
}

/** 缩放/平移边界 */
const MIN_SCALE = 1;
const MAX_SCALE = 5;
/** 双指距离阈值（px）：小于该值视为点击而非捏合 */
const PINCH_MIN_DIST = 10;
/** 单指移动阈值（px）：超过才判定为平移（区分点击） */
const PAN_THRESHOLD = 10;
/** 左右滑动翻页阈值（px）：超过判定为切换上一张/下一张 */
const SWIPE_PAGE_THRESHOLD = 60;
/** 轻扫（fling）翻页：最小位移（px）与速度阈值（px/ms），快速轻扫小位移也能翻页 */
const FLING_MIN_DISTANCE = 20;
const FLING_VELOCITY = 0.5;
/** 下滑关闭阈值（px）：未放大态竖直拖拽超过即关闭 */
const DISMISS_THRESHOLD = 120;
/** 轻点最长按压时长（ms）；双击间隔（ms）与双击距离（px） */
const TAP_DURATION = 250;
const DOUBLE_TAP_INTERVAL = 300;
const DOUBLE_TAP_DISTANCE = 40;
/** 捏合松手吸附复位阈值：缩放低于该值复位 1x */
const SNAP_SCALE_THRESHOLD = 1.05;

type GestureMode = 'idle' | 'pinch' | 'pan' | 'tap' | 'swipe' | 'dismiss';

/**
 * 现代化图片预览（自定义实现，简洁/兼容性好）：
 * - 全屏遮罩 + 点击遮罩关闭、Esc 关闭
 * - 双指捏合缩放（1x-4x，以两指中心为锚点）
 * - 单指平移（放大状态下，超阈值才触发，轻点不误触）
 * - 双击放大/还原
 * - 左右滑动切换上一张/下一张（未放大态，跟手+回弹；边界阻尼）
 * - 下载按钮
 * - 触摸不穿透到下层编辑器：
 *   - body 滚动锁定（overflow hidden）
 *   - overlay 全屏捕获 pointer 事件（底层 useSwipeGesture 的 capture 监听
 *     绑定在编辑器元素上，事件目标在 overlay 内时不会触发）
 *   - capture 阶段拦截 overlay 外的 pointer/touch（双保险）
 */
export const ImageLightbox: React.FC<ImageLightboxProps> = ({ src, alt, onClose, images, initialIndex }) => {
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  // ★ 多图浏览：当前索引 + 已解析 URL 缓存（oss:// 引用按需解析，本地缓存命中即秒回）
  const [galleryIndex, setGalleryIndex] = useState(() => {
    const idx = initialIndex ?? -1;
    return idx >= 0 && idx < (images?.length ?? 0) ? idx : 0;
  });
  const [displaySrc, setDisplaySrc] = useState(src);
  // ★ 翻页解析失败状态（显示"点击重试"，不再停留旧图或卡 loading）
  const [resolveFailed, setResolveFailed] = useState(false);
  // ★ 当前显示 URL 的 ref：解析结果与当前 URL 相同（重复图片）时直接标记 loaded，
  // 避免 img src 不变不触发 onLoad 导致卡 loading
  const displaySrcRef = useRef(src);
  // ★ 未放大态左右滑动跟手偏移（松手超阈值翻页，否则回弹）
  const [swipeOffset, setSwipeOffset] = useState(0);
  // ★ 手势期间最新值镜像（手势回调读 ref，避免闭包旧 state）
  const scaleRef = useRef(1);
  const translateRef = useRef({ x: 0, y: 0 });
  // 手势进行中（有指针按下）：transform 过渡禁用（跟手零延迟）
  const [gestureActive, setGestureActive] = useState(false);
  // 拖拽（滑动/平移/捏合/下拉）刚结束：消费紧随的合成 click，防误关
  const justDraggedRef = useRef(false);
  // pointer 级双击已处理的时间戳：抑制随后的 dblclick 二次切换
  const lastDoubleTapHandledT = useRef(0);
  // ★ 保存成功提示（Lightbox 内顶层显示，避免被全局 toast 层级遮挡）
  const [savedTip, setSavedTip] = useState<string | null>(null);
  // ★ 相邻页解析完成后的重渲染触发器（resolvedMapRef 是可变 ref，需 state 触发更新）
  const [, setNeighborTick] = useState(0);

  const galleryImages = useMemo(
    () => (images && images.length > 0 ? images : [src]),
    [images, src]
  );

  const overlayRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // 原始 src → 已解析显示 URL 缓存（同一图片来回切换不重复解析）
  const resolvedMapRef = useRef<Map<string, string>>(new Map());
  const savedTipTimerRef = useRef<number | null>(null);

  // ★ 初始 map 写入只在挂载时执行一次（mount-only 守卫）。
  // 此前每次渲染都执行：翻页后 galleryIndex 变化，会把"新图片"的缓存项
  // 污染为初始 props.src（旧图 URL）→ effect 命中污染缓存 → displaySrc 不变 →
  // img src 不触发 onLoad → loaded 永远 false → 无限"加载中"
  const mapInitRef = useRef(false);
  if (!mapInitRef.current) {
    mapInitRef.current = true;
    // 初始：当前图片已由调用方解析（props.src 即当前图的可显示 URL）
    resolvedMapRef.current.set(galleryImages[galleryIndex], src);
  }

  // ★ 防误关守卫：挂载后 300ms 内忽略遮罩点击关闭。
  // 打开预览的那次点击（pointerup → openLightbox → 本组件挂载）的合成 click
  // 可能在挂载后才派发，hit-test 落在遮罩上会"刚打开即被关闭"（模态防误触惯例）
  const closeGuardRef = useRef(false);

  // 手势状态
  const gestureRef = useRef<{
    mode: GestureMode;
    pointers: Map<number, { x: number; y: number }>;
    startDist: number;
    startScale: number;
    startMid: { x: number; y: number };
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    // swipe（未放大态翻页）最新水平偏移（松手时读取判定翻页）
    swipeDx: number;
    // pinch 起点的 translate（锚定两指中心缩放的补偿基准）
    startTranslate: { x: number; y: number };
    // tap 按下时间（区分轻点与长按拖拽）
    startT: number;
    // swipe 上一帧偏移与时间（松手计算 fling 速度）
    lastSwipeDx: number;
    lastMoveT: number;
    // 双击检测：上次轻点时间与坐标
    lastTapT: number;
    lastTapX: number;
    lastTapY: number;
    // dismiss（下滑关闭）当前竖直拖拽量
    dismissDy: number;
  }>({
    mode: 'idle',
    pointers: new Map(),
    startDist: 0,
    startScale: 1,
    startMid: { x: 0, y: 0 },
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    swipeDx: 0,
    startTranslate: { x: 0, y: 0 },
    startT: 0,
    lastSwipeDx: 0,
    lastMoveT: 0,
    lastTapT: 0,
    lastTapX: 0,
    lastTapY: 0,
    dismissDy: 0,
  });

  // ★ 打开时锁定底层交互：
  // - body 滚动锁定
  // - capture 阶段拦截 overlay 外的 pointer/touch（防止穿透到底层编辑器）
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    // 300ms 后允许遮罩点击关闭（防误关守卫）
    const guardTimer = window.setTimeout(() => {
      closeGuardRef.current = true;
    }, 300);

    const overlayEl = overlayRef.current;
    const isInsideOverlay = (e: Event): boolean => {
      const target = e.target as HTMLElement | null;
      return !!overlayEl && !!target && overlayEl.contains(target);
    };

    // capture 阶段拦截 overlay 外的事件（底层编辑器手势 / 侧滑 / 参考编辑器拉出）
    const blockPointerCapture = (e: PointerEvent) => {
      if (!isInsideOverlay(e)) {
        e.stopPropagation();
      }
    };
    const blockTouchCapture = (e: TouchEvent) => {
      if (!isInsideOverlay(e)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // ★ 问题 3：阻止编辑器聚焦（点击预览/下载不弹键盘）
    // 打开时强制 blur 编辑器（若有），并阻止 focus 事件（overlay 外）
    const prevActive = document.activeElement as HTMLElement | null;
    if (prevActive && (prevActive.isContentEditable || prevActive.closest('.ProseMirror'))) {
      prevActive.blur();
    }
    const blockFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && !isInsideOverlay(e) && (target.isContentEditable || target.closest('.ProseMirror'))) {
        e.preventDefault();
        e.stopPropagation();
        // 确保编辑器不获得焦点（不弹键盘）
        (target as HTMLElement).blur();
      }
    };
    const blockMouseDownFocus = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && !isInsideOverlay(e) && target.closest('.ProseMirror')) {
        e.preventDefault();
      }
    };

    document.addEventListener('focusin', blockFocus, true);
    document.addEventListener('mousedown', blockMouseDownFocus, true);

    document.addEventListener('pointerdown', blockPointerCapture, true);
    document.addEventListener('touchstart', blockTouchCapture, true);
    document.addEventListener('touchmove', blockTouchCapture, true);

    return () => {
      window.clearTimeout(guardTimer);
      closeGuardRef.current = false;
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
      document.removeEventListener('focusin', blockFocus, true);
      document.removeEventListener('mousedown', blockMouseDownFocus, true);
      document.removeEventListener('pointerdown', blockPointerCapture, true);
      document.removeEventListener('touchstart', blockTouchCapture, true);
      document.removeEventListener('touchmove', blockTouchCapture, true);
    };
  }, []);

  // ★ 翻页：切换 galleryIndex（缩放/平移/滑动/加载状态由 galleryIndex effect 统一重置）
  const goToIndex = useCallback(
    (next: number) => {
      if (next < 0 || next >= galleryImages.length) return;
      if (next === galleryIndex) return;
      setGalleryIndex(next);
    },
    [galleryImages, galleryIndex]
  );

  // ★ 解析当前图片（翻页 effect 与失败重试共用）：
  // - map 缓存命中 / 非 oss:// 引用 → 直接显示
  // - oss:// 引用 → imageService.resolveImageUrl（本地缓存优先，未命中从 OSS 下载一次并缓存）
  // - 成功：URL 与当前相同（重复图片）直接标记 loaded，否则切换 displaySrc
  // - 失败：显示"点击重试"，不再停留旧图
  // latestResolveIdxRef 序列守卫：快速翻页时丢弃过期解析结果（避免旧结果覆盖当前图）
  const latestResolveIdxRef = useRef(-1);
  const resolveCurrentImage = useCallback(
    (index: number) => {
      const raw = galleryImages[index];
      if (raw === undefined) return;
      latestResolveIdxRef.current = index;
      setResolveFailed(false);

      const cached = resolvedMapRef.current.get(raw);
      if (cached) {
        if (cached === displaySrcRef.current) {
          setLoaded(true);
        } else {
          setLoaded(false);
          setDisplaySrc(cached);
        }
        return;
      }
      if (!raw.startsWith('oss://')) {
        resolvedMapRef.current.set(raw, raw);
        if (raw === displaySrcRef.current) {
          setLoaded(true);
        } else {
          setLoaded(false);
          setDisplaySrc(raw);
        }
        return;
      }

      setLoaded(false);
      imageService
        .resolveImageUrl(raw.replace('oss://', ''))
        .then((url) => {
          if (url) {
            // 无论是否过期都记录缓存（翻回来秒显）
            resolvedMapRef.current.set(raw, url);
            // 过期结果（用户已翻到其他图片）：不更新显示
            if (latestResolveIdxRef.current !== index) return;
            if (url === displaySrcRef.current) {
              setLoaded(true);
            } else {
              setDisplaySrc(url);
            }
          } else {
            if (latestResolveIdxRef.current !== index) return;
            setResolveFailed(true);
            setLoaded(true);
          }
        })
        .catch(() => {
          if (latestResolveIdxRef.current !== index) return;
          setResolveFailed(true);
          setLoaded(true);
        });
    },
    [galleryImages]
  );

  // ★ 预解析相邻页（idx±1）：把结果写入 resolvedMapRef 缓存 + 触发重渲染，
  // 使轨道中的相邻图在拖拽露出前就已就位；翻页到该张时 resolveCurrentImage 直接命中秒显
  const resolveNeighbors = useCallback(
    (idx: number) => {
      for (const raw of [galleryImages[idx - 1], galleryImages[idx + 1]]) {
        if (!raw) continue;
        if (resolvedMapRef.current.has(raw)) continue;
        if (!raw.startsWith('oss://')) {
          resolvedMapRef.current.set(raw, raw);
          continue;
        }
        imageService
          .resolveImageUrl(raw.replace('oss://', ''))
          .then((url) => {
            if (url && resolvedMapRef.current.set(raw, url)) {
              setNeighborTick((t) => t + 1);
            }
          })
          .catch(() => {
            /* 相邻页解析失败不阻塞拖拽，翻到该张时由失败重试兜底 */
          });
      }
    },
    [galleryImages]
  );

  // 翻页/挂载时解析当前图片（缩放/平移/滑动状态统一重置）并预解析相邻页
  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setSwipeOffset(0);
    setLoaded(false);
    resolveCurrentImage(galleryIndex);
    resolveNeighbors(galleryIndex);
  }, [galleryIndex, resolveCurrentImage, resolveNeighbors]);

  // displaySrc 变化时同步 ref
  useEffect(() => {
    displaySrcRef.current = displaySrc;
  }, [displaySrc]);

  // scale/translate 变化时同步 ref（手势回调读取最新值，避免闭包旧 state）
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);
  useEffect(() => {
    translateRef.current = translate;
  }, [translate]);

  // ★ 放大态平移边界钳制：图片缩放后边缘不超出视口（小于视口的方向强制居中 0）。
  // 图片 transform 为 S·p + T（视口中心为原点），故偏移上限 = (缩放尺寸 - 视口尺寸) / 2
  const clampTranslate = useCallback((t: { x: number; y: number }, s: number) => {
    const img = imgRef.current;
    const ov = overlayRef.current;
    if (!img || !ov) return t;
    const maxX = Math.max(0, (img.offsetWidth * s - ov.clientWidth) / 2);
    const maxY = Math.max(0, (img.offsetHeight * s - ov.clientHeight) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, t.x)),
      y: Math.min(maxY, Math.max(-maxY, t.y)),
    };
  }, []);

  // 卸载时清理提示定时器
  useEffect(() => {
    return () => {
      if (savedTipTimerRef.current !== null) {
        window.clearTimeout(savedTipTimerRef.current);
      }
    };
  }, []);

  // Esc 关闭（有缩放先还原）；左右方向键翻页（桌面端）
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (scale > 1) {
          setScale(1);
          setTranslate({ x: 0, y: 0 });
        } else {
          onClose();
        }
      } else if (e.key === 'ArrowLeft') {
        goToIndex(galleryIndex - 1);
      } else if (e.key === 'ArrowRight') {
        goToIndex(galleryIndex + 1);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, scale, goToIndex, galleryIndex]);

  // ★ 保存成功提示：Lightbox 内部顶层显示（z-index 高于图片/按钮），
  // 修复全局 toast 被预览遮罩（z-index 9999）遮挡的问题
  const showSavedTip = useCallback((text: string) => {
    setSavedTip(text);
    if (savedTipTimerRef.current !== null) {
      window.clearTimeout(savedTipTimerRef.current);
    }
    savedTipTimerRef.current = window.setTimeout(() => {
      setSavedTip(null);
      savedTipTimerRef.current = null;
    }, 2000);
  }, []);

  // ★ 下载（问题 4）：系统保存对话框（SAF）→ 用户选择位置 → 写入
  // 数据来源优先级（全部本地，不从 OSS 重新下载）：
  // 1. 直接读本地缓存原始文件（fetch asset:// —— 预览 src 即本地缓存 URL）：
  //    保留原始格式与字节，无重编码损耗
  // 2. 兜底：从已加载 img 元素 canvas 提取（跨协议可靠，但会重编码）
  // ★ 文件名（修复"下载后是 png 格式 + 每次文件名都不同"）：
  //    优先从 URL 提取原始文件名（内容哈希命名，如 {hash}.webp——上传管线已统一
  //    转 WebP，保留 .webp 扩展名即保住真实格式）；提取不到时按 blob 实际
  //    MIME 推断扩展名，不再写死 .png；时间戳仅作提取失败时的兜底命名
  const handleDownload = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const imgEl = imgRef.current;
      let blob: Blob | null = null;

      // 1. 优先读本地缓存原始文件（asset:// 本地读取，无网络请求）
      try {
        const response = await fetch(displaySrc);
        if (response.ok) {
          blob = await response.blob();
        }
      } catch {
        blob = null;
      }

      // ★ 从 URL 提取原始文件名（asset://.../{hash}.webp、https://.../{hash}.webp?e=...）：
      // 内容哈希命名跨登录稳定；query（签名参数）不参与
      const extractFilename = (url: string): string | null => {
        try {
          const path = url.split('?')[0];
          const name = decodeURIComponent(path.split('/').pop() || '');
          return /^[^/\\]+\.[a-z0-9]{2,5}$/i.test(name) ? name : null;
        } catch {
          return null;
        }
      };
      // 按 blob 实际 MIME 推断扩展名（不再写死 .png）
      const blobExt = (b: Blob): string => {
        if (b.type.includes('webp')) return 'webp';
        if (b.type.includes('png')) return 'png';
        if (b.type.includes('jpeg')) return 'jpg';
        if (b.type.includes('gif')) return 'gif';
        return 'webp'; // 未知（octet-stream 等）：上传管线统一 WebP
      };

      // 2. 兜底：从已加载的 img 元素 canvas 提取
      //    ★ 保持原始格式优先（.webp 图导出 webp，避免重编码为 png 体积暴涨）；
      //    webp 编码不受支持（旧 Safari 等）时回退 png
      if (!blob && imgEl && imgEl.complete && imgEl.naturalWidth > 0) {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = imgEl.naturalWidth;
          canvas.height = imgEl.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(imgEl, 0, 0);
            const srcName = extractFilename(displaySrc);
            const targetMime = srcName?.toLowerCase().endsWith('.webp') ? 'image/webp' : 'image/png';
            blob = await new Promise<Blob | null>((resolve) => {
              canvas.toBlob((b) => {
                if (b || targetMime === 'image/png') {
                  resolve(b);
                } else {
                  canvas.toBlob((b2) => resolve(b2), 'image/png');
                }
              }, targetMime);
            });
          }
        } catch {
          blob = null;
        }
      }

      if (!blob) {
        console.error('图片数据获取失败:', displaySrc);
        return;
      }

      // ★ 保存到系统相册（问题 2：下载目录指向相册而非 Download）
      // Android 用 MediaStore 存相册；桌面/其它平台回退下载目录
      const bytes = new Uint8Array(await blob.arrayBuffer());
      // ★ 文件名：优先原始文件名（{hash}.webp，格式正确且跨下载稳定）；
      // 提取不到时按 blob 实际 MIME 命名（时间戳仅兜底）
      const defaultName = extractFilename(displaySrc) || `image_${Date.now()}.${blobExt(blob)}`;
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        const savedPath = await invoke<string>('save_image_to_gallery', {
          imageData: Array.from(bytes),
          filename: defaultName,
        });
        console.log('图片已保存到:', savedPath);
        showSavedTip('已保存到相册');
      } catch (saveErr) {
        console.error('保存到相册失败，回退静默保存:', saveErr);
        // 回退：saveImage（保存到应用下载目录）
        const result = await saveImage(blob, defaultName);
        if (result.success) {
          console.log('图片保存成功:', result.path || result.message);
          showSavedTip('图片已保存');
        } else {
          console.error('图片保存失败:', result.message);
        }
      }
    } catch (error) {
      console.error('保存图片时出错:', error);
    } finally {
      setSaving(false);
    }
  }, [saving, displaySrc, showSavedTip]);

  // ★ Pointer 事件（统一处理鼠标 + 触摸，现代标准）
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      const st = gestureRef.current;
      st.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      setGestureActive(true);
      // 新手势开始：清除上一次拖拽的 click 抑制标记
      justDraggedRef.current = false;

      if (st.pointers.size === 2) {
        // 双指 → pinch 预备（滑动中途加指同样进入，清掉未完成的跟手偏移）
        const [p1, p2] = [...st.pointers.values()];
        st.mode = 'pinch';
        st.startDist = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
        st.startScale = scaleRef.current;
        st.startMid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        st.startTranslate = { ...translateRef.current };
        if (st.swipeDx !== 0) {
          st.swipeDx = 0;
          setSwipeOffset(0);
        }
      } else if (st.pointers.size === 1 && st.mode !== 'pinch') {
        // 单指 → tap 预备（等移动判定）
        st.mode = 'tap';
        st.startX = e.clientX;
        st.startY = e.clientY;
        st.lastX = e.clientX;
        st.lastY = e.clientY;
        st.startT = Date.now();
      }
    },
    []
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!gestureRef.current.pointers.has(e.pointerId)) return;
      e.stopPropagation();
      const st = gestureRef.current;
      st.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (st.mode === 'pinch' && st.pointers.size >= 2) {
        // ★ 双指捏合 → 缩放，锚定两指中心（手指下内容不漂移）：
        // T = M − (M − T0) × k（M 为实时中点相对视口中心坐标，k = 新旧缩放比）
        const [p1, p2] = [...st.pointers.values()];
        const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
        if (dist > PINCH_MIN_DIST) {
          const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, st.startScale * (dist / st.startDist)));
          const mx = (p1.x + p2.x) / 2 - window.innerWidth / 2;
          const my = (p1.y + p2.y) / 2 - window.innerHeight / 2;
          const k = newScale / st.startScale;
          setScale(newScale);
          setTranslate(
            clampTranslate(
              {
                x: mx - (mx - st.startTranslate.x) * k,
                y: my - (my - st.startTranslate.y) * k,
              },
              newScale
            )
          );
        }
      } else if (st.mode === 'tap') {
        // 单指移动超阈值 → 进入 pan（放大态）/ swipe（未放大态水平主导）/ dismiss（未放大态竖直下滑）
        const dx = e.clientX - st.startX;
        const dy = e.clientY - st.startY;
        if (Math.hypot(dx, dy) > PAN_THRESHOLD) {
          if (scaleRef.current > 1) {
            st.mode = 'pan';
          } else if (dy > 0 && Math.abs(dy) > Math.abs(dx)) {
            // ★ 未放大态下滑 → 关闭拖拽（图片跟手 + 背景渐隐）
            st.mode = 'dismiss';
            st.dismissDy = 0;
          } else if (galleryImages.length > 1 && Math.abs(dx) > Math.abs(dy)) {
            st.mode = 'swipe';
            st.swipeDx = 0;
            st.lastSwipeDx = 0;
            st.lastMoveT = Date.now();
          }
        }
      }
      if (st.mode === 'pan') {
        // ★ 放大态平移：边界钳制（图片永远不会被拖出屏幕）
        const dx = e.clientX - st.lastX;
        const dy = e.clientY - st.lastY;
        st.lastX = e.clientX;
        st.lastY = e.clientY;
        setTranslate((prev) => clampTranslate({ x: prev.x + dx, y: prev.y + dy }, scaleRef.current));
      } else if (st.mode === 'swipe') {
        // ★ 未放大态水平滑动跟手：图片跟随手指移动；边界（首/末张）阻尼 0.3
        const dx = e.clientX - st.startX;
        let d = dx;
        if ((galleryIndex === 0 && d > 0) || (galleryIndex === galleryImages.length - 1 && d < 0)) {
          d = d * 0.3;
        }
        st.lastSwipeDx = st.swipeDx;
        st.lastMoveT = Date.now();
        st.swipeDx = d;
        setSwipeOffset(d);
      } else if (st.mode === 'dismiss') {
        // ★ 下滑关闭跟手：竖直全跟手，水平轻微跟手（限幅）
        const dx = e.clientX - st.startX;
        const dy = Math.max(0, e.clientY - st.startY);
        st.dismissDy = dy;
        setTranslate({ x: Math.max(-60, Math.min(60, dx * 0.4)), y: dy });
      }
    },
    [galleryImages.length, galleryIndex, clampTranslate]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      const st = gestureRef.current;
      st.pointers.delete(e.pointerId);
      if (st.pointers.size === 0) {
        if (st.mode === 'swipe') {
          // ★ 松手判定：位移超阈值或快速轻扫（fling）→ 翻页；否则回弹
          const d = st.swipeDx;
          const dt = Date.now() - st.lastMoveT;
          const velocity = Math.abs(d - st.lastSwipeDx) / Math.max(dt, 1); // px/ms
          const dir = d < 0 ? 1 : -1;
          const next = galleryIndex + dir;
          const shouldPage =
            next >= 0 &&
            next < galleryImages.length &&
            (Math.abs(d) >= SWIPE_PAGE_THRESHOLD ||
              (Math.abs(d) >= FLING_MIN_DISTANCE && velocity >= FLING_VELOCITY));
          if (shouldPage) {
            // ★ 同批提交索引切换与滑动归零：轨道直接以 transition 平滑过渡到下一张定位，
            // 相邻图已在拖拽过程中紧随露出，翻页无跳变
            setSwipeOffset(0);
            goToIndex(next);
          } else {
            setSwipeOffset(0);
          }
          st.swipeDx = 0;
          if (Math.abs(d) > PAN_THRESHOLD) justDraggedRef.current = true;
        } else if (st.mode === 'dismiss') {
          // ★ 下滑超阈值 → 关闭；否则回弹复位
          if (st.dismissDy > DISMISS_THRESHOLD) {
            onClose();
            return;
          }
          setTranslate({ x: 0, y: 0 });
          justDraggedRef.current = true;
        } else if (st.mode === 'pinch') {
          // 全部抬起：缩放接近 1x → 吸附复位（清平移偏移）
          if (scaleRef.current < SNAP_SCALE_THRESHOLD) {
            setScale(1);
            setTranslate({ x: 0, y: 0 });
          }
          justDraggedRef.current = true;
        } else if (st.mode === 'pan') {
          justDraggedRef.current = true;
        } else if (st.mode === 'tap') {
          // ★ 双击检测（pointer 级，触屏可靠）：两次轻点间隔/距离内 → 锚定点击位置缩放
          const dur = Date.now() - st.startT;
          const dist = Math.hypot(e.clientX - st.startX, e.clientY - st.startY);
          if (dur < TAP_DURATION && dist < PAN_THRESHOLD) {
            const now = Date.now();
            const isDoubleTap =
              now - st.lastTapT < DOUBLE_TAP_INTERVAL &&
              Math.hypot(e.clientX - st.lastTapX, e.clientY - st.lastTapY) < DOUBLE_TAP_DISTANCE;
            st.lastTapT = now;
            st.lastTapX = e.clientX;
            st.lastTapY = e.clientY;
            if (isDoubleTap && e.target === imgRef.current) {
              st.lastTapT = 0; // 防三击连击
              lastDoubleTapHandledT.current = now; // 抑制随后合成的 dblclick
              if (scaleRef.current > 1) {
                setScale(1);
                setTranslate({ x: 0, y: 0 });
              } else {
                // 锚定双击点放大 2x：T = M − M × 2（M 相对视口中心）
                const mx = e.clientX - window.innerWidth / 2;
                const my = e.clientY - window.innerHeight / 2;
                setScale(2);
                setTranslate(clampTranslate({ x: mx - mx * 2, y: my - my * 2 }, 2));
              }
            }
          } else {
            // 拖拽超阈值的 tap → 标记抑制合成 click
            if (dist >= PAN_THRESHOLD) justDraggedRef.current = true;
          }
        }
        st.mode = 'idle';
        setGestureActive(false);
      } else if (st.pointers.size === 1 && st.mode === 'pinch') {
        // ★ 双指抬起一指：放大态无缝接续单指平移（剩余手指坐标为锚），未放大则结束
        const [remaining] = [...st.pointers.values()];
        if (scaleRef.current > 1 && remaining) {
          st.mode = 'pan';
          st.lastX = remaining.x;
          st.lastY = remaining.y;
        } else {
          st.mode = 'idle';
          setGestureActive(false);
        }
      }
    },
    [galleryImages.length, galleryIndex, goToIndex, onClose, clampTranslate]
  );

  // ★ 双击放大/还原（桌面鼠标；触屏双击由 pointer 双击检测处理，此处抑制重复切换）
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (Date.now() - lastDoubleTapHandledT.current < 500) return;
      if (scale > 1) {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
      } else {
        setScale(2);
        setTranslate({ x: 0, y: 0 });
      }
    },
    [scale]
  );

  // ★ 下滑关闭时背景渐隐（未放大态竖直拖拽量 → 遮罩透明度）
  const dismissDyNow = scale === 1 ? Math.max(0, translate.y) : 0;
  const bgAlpha = 0.78 * (1 - 0.55 * Math.min(1, dismissDyNow / 500));

  // ★ 视口宽度：挂载后实测 + resize 跟随（旋转/窗口缩放不失配；
  //   此前渲染期读 ref 首帧为 null 只能回退 innerWidth）
  const [viewW, setViewW] = useState(() => window.innerWidth);
  useLayoutEffect(() => {
    const measure = () => {
      const w = overlayRef.current?.clientWidth ?? window.innerWidth;
      setViewW((prev) => (prev === w ? prev : w));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // ★ 全量稳定轨道：槽位 i 恒等于图片 i（DOM 位置与画廊索引永久对齐）。
  //   此前窗口化渲染 [idx-1,idx,idx+1] 但 transform 用 -galleryIndex*viewW，
  //   索引 ≥2 时当前页被平移出屏（预览空白）且后一页恰好落屏（切换错位）。
  //   全量轨道使 transform 恒正确，且翻页时槽位内容不漂移——出图继续滑出、
  //   入图继续滑入（现代轮播的连续手势语义）
  const trackX = -galleryIndex * viewW + swipeOffset;
  // ★ 分页空区点击关闭：轨道/分页铺满全屏后，点图外空白 target 落在 page 上而非 overlay，
  // 这里补齐与 overlay 一致的关闭语义（跳过 img/按钮子树）
  const handlePageClick = useCallback(
    (e: React.MouseEvent) => {
      if (justDraggedRef.current) {
        justDraggedRef.current = false;
        return;
      }
      if (e.target !== e.currentTarget) return;
      if (closeGuardRef.current) onClose();
    },
    [onClose]
  );

  return (
    <div
      ref={overlayRef}
      className="image-lightbox-overlay"
      // ★ 穿透防护由 document 级 capture 拦截承担（blockPointerCapture/blockTouchCapture
      //   检查 isInsideOverlay，仅拦截 overlay 外事件）。
      //   注意：不能在 overlay 自身挂 onPointerDownCapture/onPointerUpCapture 并 stopPropagation——
      //   React 捕获阶段的 stopPropagation 会调用原生 stopPropagation，把 overlay 子树内
      //   （图片/按钮上）的 pointerdown/up 在到达目标前拦截，导致 handlePointerDown 等
      //   手势处理器永远收不到事件，缩放/平移/翻页/下滑关闭全部失效。
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDoubleClick={handleDoubleClick}
      onClick={(e) => {
        // 拖拽（滑动/平移/捏合/下拉）刚结束：丢弃紧随的合成 click，防回弹瞬间误关
        if (justDraggedRef.current) {
          justDraggedRef.current = false;
          return;
        }
        // 点击遮罩（非图片/按钮）关闭（挂载初期 300ms 内忽略，防打开瞬间的合成 click 误关）
        if (e.target === overlayRef.current && closeGuardRef.current) {
          onClose();
        }
      }}
      onTouchMove={(e) => e.preventDefault()}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: `rgba(0, 0, 0, ${bgAlpha})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        cursor: 'zoom-in',
        animation: 'lightboxFadeIn 0.25s ease-out',
        touchAction: 'none',
        overflow: 'hidden',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
    >
      {/* ★ 全量稳定轨道：横向轨道每槽位恒等于对应图片（DOM 位置与画廊索引永久对齐），
         拖拽时整体 translateX 跟手，相邻图从对侧重合地紧随露出；翻页为轨道目标位移的
         平滑 transition（无跳变、槽位内容不漂移）。资源窗口化：仅当前 ±1 槽位挂载
         <img>（近邻由 resolveNeighbors 预解析），远端槽位为轻量空 div。
         当前页仍保留缩放/平移/加载/重试/下载语义（放大占满全屏）。 */}
      <div
        className="lightbox-track"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: galleryImages.length * viewW,
          transform: `translateX(${trackX}px)`,
          willChange: 'transform',
          // 手势跟手（滑动偏移/捏合/平移/下拉进行中）禁用过渡（零延迟）；
          // 松手后的回弹、翻页切换走 0.25s ease-out（平滑现代手感）
          transition: swipeOffset !== 0 || gestureActive
            ? 'none'
            : 'transform 0.25s ease-out',
        }}
      >
        {galleryImages.map((raw, pageIdx) => {
          const isCur = pageIdx === galleryIndex;
          const isNeighbor = Math.abs(pageIdx - galleryIndex) === 1;
          const pageStyle: React.CSSProperties = {
            flex: `0 0 ${viewW}px`,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
          };
          // 远端槽位：轻量空 div（不挂 img）；向其翻页时自动成为近邻并预解析
          if (!isCur && !isNeighbor) {
            return (
              <div key={pageIdx} className="lightbox-page" onClick={handlePageClick} style={pageStyle} />
            );
          }
          // 当前页：缩放/平移/加载/重试都作用于它
          if (isCur) {
            return (
              <div key={pageIdx} className="lightbox-page" onClick={handlePageClick} style={pageStyle}>
                <img
                  ref={imgRef}
                  src={displaySrc}
                  alt={alt || ''}
                  onLoad={() => {
                    setLoaded(true);
                  }}
                  onError={() => {
                    // 图片获取失败（如本地缓存文件损坏/路径失效）：停 loading 并显示重试
                    setLoaded(true);
                    setResolveFailed(true);
                  }}
                  onDoubleClick={handleDoubleClick}
                  draggable={false}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    maxWidth: '92vw',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                    cursor: scale > 1 ? 'grab' : 'zoom-in',
                    // transform 锚点为图片中心：translate(-50%,-50%) + 平移偏移 + 缩放；
                    // 横向翻页由轨道整体平移驱动（swipeOffset 已移出本处，避免双重平移）
                    transform: `translate(calc(-50% + ${translate.x}px), calc(-50% + ${translate.y}px)) scale(${scale})`,
                    transition: swipeOffset !== 0 || gestureActive
                      ? 'none'
                      : 'transform 0.25s ease-out',
                    transformOrigin: 'center center',
                    display: loaded && !resolveFailed ? 'block' : 'none',
                    touchAction: 'none',
                    pointerEvents: 'auto',
                  }}
                />
                {/* 加载动态指示（解析中或图片加载中） */}
                {!loaded && !resolveFailed && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '14px',
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid rgba(255,255,255,0.25)',
                        borderTopColor: '#ffffff',
                        borderRadius: '50%',
                        animation: 'lightboxSpin 0.8s linear infinite',
                      }}
                    />
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '14px' }}>加载中...</div>
                  </div>
                )}
                {/* 翻页解析失败：点击重试（不再停留旧图或无限 loading） */}
                {resolveFailed && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      // 清除当前图缓存项后重新解析（覆盖"缓存 URL 已失效"场景）
                      const rawCur = galleryImages[galleryIndex];
                      if (rawCur !== undefined) {
                        resolvedMapRef.current.delete(rawCur);
                      }
                      resolveCurrentImage(galleryIndex);
                    }}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      zIndex: 2,
                      cursor: 'pointer',
                      padding: '20px 28px',
                      borderRadius: '12px',
                      background: 'rgba(0,0,0,0.45)',
                    }}
                  >
                    <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '15px' }}>图片加载失败</div>
                    <div
                      style={{
                        color: '#ffffff',
                        fontSize: '14px',
                        padding: '8px 18px',
                        borderRadius: '8px',
                        background: 'rgba(255,255,255,0.18)',
                      }}
                    >
                      点击重试
                    </div>
                  </div>
                )}
              </div>
            );
          }
          // 相邻页：仅居中展示已解析 URL（未就绪时占位），无手势交互；翻页由轨道平移完成
          const neighborSrc = resolvedMapRef.current.get(raw ?? '') ?? '';
          return (
            <div key={pageIdx} className="lightbox-page" onClick={handlePageClick} style={pageStyle}>
              {neighborSrc ? (
                <img
                  src={neighborSrc}
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    maxWidth: '92vw',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                  }}
                />
              ) : (
                <div style={{ color: 'rgba(255,255,255,0.18)', fontSize: '14px' }}>加载中...</div>
              )}
            </div>
          );
        })}
      </div>

      {/* ★ 保存成功提示（顶层显示，z-index 高于图片/按钮——修复被 toast 层级遮挡） */}
      {savedTip && (
        <div
          style={{
            position: 'absolute',
            top: '24px',
            right: '24px',
            background: 'rgba(16,185,129,0.92)',
            color: 'white',
            padding: '10px 18px',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: 500,
            pointerEvents: 'none',
            zIndex: 4,
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            animation: 'lightboxFadeIn 0.2s ease-out',
          }}
        >
          {savedTip}
        </div>
      )}

      {/* ★ 页码指示（多图时显示） */}
      {galleryImages.length > 1 && (
        <div
          style={{
            position: 'absolute',
            top: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.6)',
            color: 'rgba(255,255,255,0.9)',
            padding: '6px 14px',
            borderRadius: '999px',
            fontSize: '13px',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          {galleryIndex + 1} / {galleryImages.length}
        </div>
      )}

      {/* ★ 左右切换箭头（多图时显示；桌面端鼠标/触摸均可点击） */}
      {galleryImages.length > 1 && galleryIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToIndex(galleryIndex - 1);
          }}
          aria-label="上一张"
          title="上一张"
          style={{
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '44px',
            height: '44px',
            background: 'rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: '50%',
            color: 'white',
            fontSize: '22px',
            lineHeight: 1,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
            zIndex: 2,
          }}
        >
          ‹
        </button>
      )}
      {galleryImages.length > 1 && galleryIndex < galleryImages.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToIndex(galleryIndex + 1);
          }}
          aria-label="下一张"
          title="下一张"
          style={{
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '44px',
            height: '44px',
            background: 'rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: '50%',
            color: 'white',
            fontSize: '22px',
            lineHeight: 1,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
            zIndex: 2,
          }}
        >
          ›
        </button>
      )}

      {/* 缩放提示（放大态显示） */}
      {scale > 1 && (
        <div
          style={{
            position: 'absolute',
            bottom: '76px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.6)',
            color: 'white',
            padding: '6px 14px',
            borderRadius: '999px',
            fontSize: '12px',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          双击还原 · 双指缩放
        </div>
      )}

      {/* 下载按钮 */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDownload();
        }}
        disabled={saving}
        aria-label="下载图片"
        title="下载图片"
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '48px',
          height: '48px',
          background: saving ? 'rgba(59,130,246,0.8)' : 'rgba(0,0,0,0.7)',
          border: '2px solid rgba(255,255,255,0.2)',
          borderRadius: '50%',
          cursor: saving ? 'wait' : 'pointer',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
          zIndex: 2,
        }}
      >
        {saving ? (
          <div
            style={{
              width: '22px',
              height: '22px',
              border: '2px solid rgba(255,255,255,0.4)',
              borderTopColor: '#fff',
              borderRadius: '50%',
              animation: 'lightboxSpin 1s linear infinite',
            }}
          />
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 16L7 11L8.5 12.5L12 16L15.5 12.5L17 11M12 16V4M5 20H19" />
          </svg>
        )}
      </button>

      <style>{`
        @keyframes lightboxFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes lightboxSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
